/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import _get from 'lodash/get';
import { connect } from 'react-redux';
import { useDrag } from 'react-dnd';
import PropTypes from 'prop-types';

import ColumnItemHighlighter from './ColumnItemHighlighter';
import ColumnItemControls from './ColumnItemControls';
import Element from './Element/Element';
import * as columnItemActions from '../../../../../../actions/columnItem';
import * as itemTypes from '../../../../../../constants/itemTypes/itemTypes';
import businessRules from '../../../../../../constants/businessRules/businessRules';
import { ERR_COLUMN_ITEM_MAX_ELEMENTS } from '../../../../../../constants/Errors';
import eventService from '../../../../../../common/eventService';
import { elementContentType } from '../../../../../../types';
import * as Styled from './styles';
import elementTypes from '../../../../../../constants/elementTypes';

const ColumnItem = (props) => {
    const [hoverState, setHoverState] = useState(false);
    const {
        blockIndex,
        columnIdx,
        structureLocked,
        elmIdx,
        numElements,
        content,
        type,
        width,
        selectedBlocks,
        elementIsSelected,
        multipleElementsSelected,
        dispatch,
        isInfoLoaded,
        hasCustomFontsAccess,
    } = props;
    const elementIsInSelectedBlocks = selectedBlocks.includes(blockIndex);
    const shouldRenderColumnItemControls =
        !structureLocked && elementIsInSelectedBlocks && elementIsSelected && !multipleElementsSelected;

    const isElementImage = type === elementTypes.IMAGE || type === elementTypes.VIDEO;
    let imageHeight;
    let imageContainerHeight;
    if (isElementImage) {
        const { aspectRatio } = content;
        const imageWidth = width - (content.paddingLeft + content.paddingRight);
        imageHeight = !aspectRatio
            ? parseInt(imageWidth, 10) > 150
                ? 150
                : parseInt(imageWidth, 10)
            : parseInt(imageWidth / aspectRatio, 10);
        imageContainerHeight = imageHeight + (content.paddingTop + content.paddingBottom);
    }

    const [{ isDragging }, drag] = useDrag({
        type: itemTypes.CUSTOMIZED_BLOCK_COLUMN_ITEM,
        item: () => ({
            draggedBlockIndex: blockIndex,
            draggedColumnIdx: columnIdx,
            draggedColumnItemIdx: elmIdx,
            content,
            itemType: type,
        }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const cursor = elementIsSelected ? 'default' : isDragging ? 'grabbing' : 'grab';
    return (
        <div
            style={{
                position: 'relative',
                height: isElementImage ? imageContainerHeight : '100%',
                opacity: isDragging ? '0.1' : '1',
                cursor,
            }}
            className={`column-item-${elmIdx}`}
        >
            <ColumnItemHighlighter
                shouldRender={elementIsSelected}
                status={multipleElementsSelected ? 'MULTIPLE_SELECTED' : 'SINGLE_SELECTED'}
            />
            {isInfoLoaded || !hasCustomFontsAccess ? (
                <Element
                    type={type}
                    blockIndex={blockIndex}
                    columnIdx={columnIdx}
                    elmIdx={elmIdx}
                    content={content}
                    width={width}
                    imageHeight={imageHeight}
                    elementIsSelected={elementIsSelected}
                    multipleElementsSelected={multipleElementsSelected}
                />
            ) : null}
            {elementIsSelected && !multipleElementsSelected && type !== elementTypes.TABLE && (
                <div
                    onMouseEnter={() => setHoverState(true)}
                    onMouseLeave={() => setHoverState(false)}
                    tabIndex="-1"
                    role="button"
                    ref={drag}
                    aria-label="move element"
                    aria-hidden
                    style={{
                        width: 29,
                        height: 29,
                        position: 'absolute',
                        backgroundColor: hoverState ? '#0277BD' : 'white',
                        zIndex: 2,
                        cursor: 'grab',
                        left: '-30px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px 0 rgba(28,28,28,0.2)',
                        display: 'flex',
                        top: 0,
                    }}
                >
                    <Styled.DragIcon className="material-icons" aria-hidden>
                        zoom_out_map
                    </Styled.DragIcon>
                </div>
            )}
            <ColumnItemControls
                shouldRender={shouldRenderColumnItemControls}
                duplicateColumnItem={() => {
                    if (numElements >= businessRules.MAXIMUM_ALLOWABLE_BLOCK_COLUMN_ITEMS) {
                        eventService.notify(ERR_COLUMN_ITEM_MAX_ELEMENTS, true);
                        return;
                    }
                    dispatch(columnItemActions.duplicateColumnItem({ blockIndex, columnIdx, elmIdx }));
                }}
                deleteColumnItem={() => {
                    dispatch(columnItemActions.deleteColumnItem({ blockIndex, columnIdx, elmIdx }));
                }}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    selectedBlocks: state.selectedBlocks,
    isInfoLoaded: _get(state, 'customFonts.isInfoLoaded', false),
    hasCustomFontsAccess: _get(state, 'session.user.features.EMAIL_DESIGNER_CUSTOM_FONTS', false),
});

ColumnItem.propTypes = {
    blockIndex: PropTypes.string.isRequired,
    columnIdx: PropTypes.string.isRequired,
    structureLocked: PropTypes.bool.isRequired,
    elmIdx: PropTypes.string.isRequired,
    numElements: PropTypes.number.isRequired,
    content: elementContentType.isRequired,
    type: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    selectedBlocks: PropTypes.arrayOf(PropTypes.string).isRequired,
    elementIsSelected: PropTypes.bool.isRequired,
    multipleElementsSelected: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    isInfoLoaded: PropTypes.bool.isRequired,
    hasCustomFontsAccess: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(ColumnItem);
